import { useUpdateCompanyInboundSettingsMutation } from '@/modules/companies/mutations/useUpdateCompanyInboundSettingsMutation';
import { useCompanyInboundSettingsQuery } from '@/modules/companies/queries/useCompanyInboundSettingsQuery';
import { HMLoadError } from '@/modules/hiring-manager/components/HMLoadError';
import { useSavedFlash } from '@/modules/ui/components/saved-indicator/useSavedFlash';
import { Skeleton } from '@/modules/ui/components/skeleton/Skeleton';
import { actionErrorMessage } from '@/utils/actionErrorMessage';

import { CareersPageSetting } from './CareersPageSetting';

interface CareersPageSectionProps {
  tenantId: string;
}

/** The self-service tenant's Careers Page on the Workspace page; the page shows it on dev only. */
export function CareersPageSection({ tenantId }: CareersPageSectionProps) {
  const settingsQuery = useCompanyInboundSettingsQuery(tenantId);
  const updateMutation = useUpdateCompanyInboundSettingsMutation();
  const { isSaved, markSaved, clearSaved } = useSavedFlash();

  const save = (isCareersPageEnabled: boolean) => {
    clearSaved();
    updateMutation.mutate(
      { companyId: tenantId, isCareersPageEnabled },
      { onSuccess: markSaved },
    );
  };

  return (
    <section
      aria-labelledby="careers-page-heading"
      className="flex flex-col gap-1"
    >
      <div className="flex flex-col gap-0.5">
        <h2 className="text-[15px] font-semibold" id="careers-page-heading">
          Job applications
        </h2>
        <p className="text-muted-foreground text-[13px]">
          Let people apply to your jobs, alongside the candidates Finlay finds.
        </p>
      </div>

      {settingsQuery.isPending ? (
        <Skeleton className="my-4 h-16 w-full" />
      ) : settingsQuery.isError || !settingsQuery.data ? (
        <HMLoadError
          description="We could not load this setting. Check your connection and try again."
          isRetrying={settingsQuery.isFetching}
          onRetry={() => void settingsQuery.refetch()}
          title="Careers Page could not be loaded"
        />
      ) : (
        <CareersPageSetting
          settings={settingsQuery.data}
          origin={window.location.origin}
          isSaving={updateMutation.isPending}
          isSaved={isSaved}
          error={
            updateMutation.isError
              ? actionErrorMessage('save this setting', updateMutation.error)
              : null
          }
          onChange={save}
          onRetry={() => save(updateMutation.variables!.isCareersPageEnabled)}
        />
      )}
    </section>
  );
}
